import { useState, useEffect, createContext } from "react";
import { useDispatch } from "react-redux";
import { useMsal } from "@azure/msal-react";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import {
  getLocalStorageItem,
  reloadApp,
  setLocalStorageItem,
  clearLocalStorageItem,
  getUserAuthPayload,
  filterEnabledPlants,
  getAccessTokenWithScope
} from "../util/helper/helperFunctions";
import { plantConfigUrl } from "../util/network/apiUrl";
import {
  setDockDoorMode,
  setDockDoorViewIsOpen,
  setEnableBatteryStationObj,
  setProductionLineViewIsOpen
} from "../store/plantSlice";
import {
  AXIOS,
  registerAuthSetterForAxios
} from "../util/network/axiosInstance";

export const AppProvider = ({ children }) => {
  const dispatch = useDispatch();
  //socket instance && **non-serializable value - do not migrate to redux store**
  const [socketConnection, setSocketConnection] = useState(null);
  const [plantConfig, setPlantConfig] = useState(null);
  const [appConfig, setAppConfig] = useState(null);
  const [locationsData, setLocationsData] = useState([]);
  const [locationCode, setLocationCode] = useState("");
  const [auth, setAuth] = useState(() => getLocalStorageItem("auth"));

  //Warehouse
  const [warehouseModelIsOpen, setWarehouseModelIsOpen] = useState(false);

  //Popups
  const [customizePopup, setCustomizePopup] = useState(false);
  const [additionalDashboardModalIsOpen, setAdditionalDashboardModalIsOpen] =
    useState(false);
  const [isLegendsModalOpen, setIsLegendsModalOpen] = useState(false);
  const [showSaveDiscardLayout, setShowSaveDiscardLayout] = useState(false);
  const [isDefaultLayoutSelected, setIsDefaultLayoutSelected] = useState(true);
  const [draggedItems, setDraggedItems] = useState([]);
  const [warehouseTempOrder, setWarehouseTempOrder] = useState(
    getLocalStorageItem("warehouse-layout-order")
  );
  const [wareHouseDraggedItems, setWareHouseDraggedItems] = useState([]);
  const [isMapDragEnable, setIsMapDragEnable] = useState(true);
  const [reRenderKey, setReRenderKey] = useState(1);

  const [timeRangePopup, setTimeRangePopup] = useState(false);

  const [isBlockRackModalOpen, setIsBlockRackModalOpen] = useState(false);
  const [isRackModal, setIsRackModal] = useState(null);
  const [layoutPopup, setLayoutPopup] = useState(false);
  const [openedBatteryStationName, setOpenedBatteryStationName] =
    useState(null);

  //utilised in both old timemachine  and v2
  const [openTimeMachineRangePopup, setOpenTimeMachineRangePopup] =
    useState(false);
  const [timeMachineTimeFrames, setTimeMachineTimeFrames] = useState({});

  //Nav Quality Heatmap
  const [navHeatmapRef, setNavHeatmapRef] = useState(null); //holds Non-Serializable

  const [isNoHeatMapDataModalOpen, setIsNoHeatMapDataModalOpen] =
    useState(false);
  //Heatmap
  const [heatmapRef, setHeatmapRef] = useState(null); //holds Non-Serializable

  //Production Line

  //Removed LGV
  const [removedLgvViewIsOpen, setRemovedLgvViewIsOpen] = useState(true);

  //Destination Pointers
  const [destinationPointersViewIsOpen, setDestinationPointersViewIsOpen] =
    useState(false);
  const [enableDestinationPointers, setEnableDestinationPointers] =
    useState(true);

  //Logic Variables
  const [selectedMarkerData, setSelectedMarkerData] = useState({});
  const [selectedMarkerType, setSelectedMarkerType] = useState("Lgv");
  const [isMarkerPopupOpen, setIsMarkerPopupOpen] = useState(false);
  const [siteSelectionModal, setSiteSelectionModal] = useState(false);

  //Internet Connectivity
  const [connectionStatus, setConnectionStatus] = useState(false);

  //Modals Minimize and Maximize
  const [minimizedWebObjectsList, setMinimizedWebObjectsList] = useState({
    indoorMap: {
      lgv: [],
      docks: [],
      productionLine: []
    },
    newTimeMachine: {
      lgv: [],
      docks: [],
      productionLine: []
    }
  });

  //Lgv CheckList
  const [enableCheckList, setEnableCheckList] = useState(true);

  //Alert
  const [alertObject, setAlertObject] = useState({ type: "", payload: {} });

  const [warehouseData, setWarehouseData] = useState();
  const [shiftPerformanceData, setShiftPerformanceData] = useState();
  const [openedLgvVehichleNumber, setOpenedLgvVehichleNumber] = useState(null);

  const customizeLayoutHandler = (state) => {
    if (isDefaultLayoutSelected) {
      //Default Layout
      if (state === "discard") {
        setIsDefaultLayoutSelected(false);
      } else {
        //save
        clearLocalStorageItem("layout-order");
        clearLocalStorageItem("warehouse-layout-order");
        setWarehouseTempOrder(null);
      }
    } else {
      //Custom Layout
      if (state === "discard") {
        clearLocalStorageItem("layout-order");
        clearLocalStorageItem("warehouse-layout-order");
        setWarehouseTempOrder(null);
        setIsDefaultLayoutSelected(true);
      } else {
        //save
        let order = [];
        let warehouseOrder = [];
        draggedItems.forEach((item) => {
          order.push(item.key);
        });
        wareHouseDraggedItems.forEach((item) => {
          warehouseOrder.push(item.key);
        });
        setLocalStorageItem("layout-order", order);
        setLocalStorageItem("warehouse-layout-order", warehouseOrder);
      }
    }

    document.body.classList.add("no-drag");
    setShowSaveDiscardLayout(false);
    setIsMapDragEnable(true);
    setReRenderKey(Math.random());
  };

  // get App Config
  const getAppConfig = async (auth, account) => {
    try {
      const userPayload = getUserAuthPayload(account.idTokenClaims);

      const response = await AXIOS.post(plantConfigUrl, userPayload);

      if (response?.status === 200 && response?.data) {
        setAppConfig(response.data.config);
        const enabledPlants = filterEnabledPlants(response.data.plants);
        setLocationsData(enabledPlants);
      } else if (response?.status === 401) {
        reloadApp();
      }
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        instance.loginRedirect();
      } else {
        console.error("Error fetching app config:", error);
      }
    }
  };

  // check App Version from LocalStorage and keep version updated
  const appVersionCheckup = () => {
    document.body.classList.add("no-drag");
    const storedVersion = getLocalStorageItem("version");
    const currentVersion = process.env.REACT_APP_VERSION;

    if (storedVersion !== currentVersion) {
      localStorage.clear();
      setLocalStorageItem("version", currentVersion);
      reloadApp();
    } else {
      setLocalStorageItem("version", currentVersion);
    }
  };

  // retrieve App Feature settings for additional dashboard into localstorage and context
  const appFeatureSettingsCheckup = () => {
    if (!localStorage.getItem("appSettings")) return;

    const prevSwitchState = JSON.parse(localStorage.getItem("appSettings"));
    setRemovedLgvViewIsOpen(Boolean(prevSwitchState.removedLgv));
    dispatch(
      setProductionLineViewIsOpen(Boolean(prevSwitchState.productionLines))
    );
    dispatch(setDockDoorViewIsOpen(Boolean(prevSwitchState.dockDoors)));
    dispatch(setDockDoorMode(Boolean(prevSwitchState.dockDoorMode)));
    setEnableDestinationPointers(Boolean(prevSwitchState.destinationLines));
    if (typeof prevSwitchState.batteryStation !== "undefined")
      dispatch(
        setEnableBatteryStationObj(Boolean(prevSwitchState.batteryStation))
      );
  };

  const { accounts, instance } = useMsal();

  useEffect(() => {
    const initializeApp = async () => {
      if (!accounts.length) return;

      try {
        localStorage.setItem("account", JSON.stringify(accounts));

        const tokenData = await getAccessTokenWithScope(instance, accounts[0]);
        if (tokenData?.accessToken) {
          setAuth(tokenData);
          localStorage.setItem("auth", JSON.stringify(tokenData));

          await getAppConfig(tokenData, accounts[0]);
        }

        appVersionCheckup();
        appFeatureSettingsCheckup();
      } catch (err) {
        console.error("App init failed:", err);
        localStorage.removeItem("auth");
        if (err instanceof InteractionRequiredAuthError) {
          instance.loginRedirect();
        }
      }
    };

    initializeApp();
  }, [accounts, instance]);

  useEffect(() => {
    if (!accounts.length || !instance) return;
    const interval = setInterval(async () => {
      try {
        const tokenData = await getAccessTokenWithScope(instance, accounts[0]);
        if (tokenData?.accessToken) {
          setAuth(tokenData);
          localStorage.setItem("auth", JSON.stringify(tokenData));
        }
      } catch (err) {
        console.error("Token refresh failed:", err);
      }
    }, 45 * 60 * 1000);
    return () => clearInterval(interval);
  }, [accounts, instance]);

  //passing setter to be utilised in AXIOS config
  useEffect(() => {
    registerAuthSetterForAxios(setAuth);
  }, []);

  return (
    <AppContext.Provider
      value={{
        socketConnection,
        setSocketConnection,
        auth,
        setAuth,
        timeRangePopup,
        setTimeRangePopup,
        timeMachineTimeFrames,
        setTimeMachineTimeFrames,
        customizePopup,
        setCustomizePopup,
        layoutPopup,
        openedBatteryStationName,
        setOpenedBatteryStationName,
        setLayoutPopup,
        isDefaultLayoutSelected,
        setIsDefaultLayoutSelected,
        customizeLayoutHandler,
        showSaveDiscardLayout,
        setShowSaveDiscardLayout,
        draggedItems,
        setDraggedItems,
        wareHouseDraggedItems,
        setWareHouseDraggedItems,
        warehouseTempOrder,
        setWarehouseTempOrder,
        reRenderKey,
        setReRenderKey,
        isMapDragEnable,
        setIsMapDragEnable,
        removedLgvViewIsOpen,
        setRemovedLgvViewIsOpen,
        warehouseModelIsOpen,
        setWarehouseModelIsOpen,
        additionalDashboardModalIsOpen,
        setAdditionalDashboardModalIsOpen,
        isLegendsModalOpen,
        setIsLegendsModalOpen,
        selectedMarkerData,
        setSelectedMarkerData,
        selectedMarkerType,
        setSelectedMarkerType,
        isMarkerPopupOpen,
        setIsMarkerPopupOpen,
        siteSelectionModal,
        setSiteSelectionModal,
        locationsData,
        plantConfig,
        setPlantConfig,
        setLocationsData,
        locationCode,
        setLocationCode,
        isNoHeatMapDataModalOpen,
        setIsNoHeatMapDataModalOpen,
        heatmapRef,
        setHeatmapRef,
        destinationPointersViewIsOpen,
        setDestinationPointersViewIsOpen,
        enableDestinationPointers,
        setEnableDestinationPointers,
        connectionStatus,
        setConnectionStatus,
        isBlockRackModalOpen,
        setIsBlockRackModalOpen,
        isRackModal,
        setIsRackModal,
        minimizedWebObjectsList,
        setMinimizedWebObjectsList,
        alertObject,
        setAlertObject,
        appConfig,
        setAppConfig,
        navHeatmapRef,
        setNavHeatmapRef,
        enableCheckList,
        setEnableCheckList,
        openTimeMachineRangePopup,
        setOpenTimeMachineRangePopup,
        warehouseData,
        setWarehouseData,
        shiftPerformanceData,
        setShiftPerformanceData,
        openedLgvVehichleNumber,
        setOpenedLgvVehichleNumber
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

const AppContext = createContext(AppProvider);
export { AppContext };
